import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "antd/lib/button";
import Empty from "antd/lib/empty";
import Form from "antd/lib/form";
import Input from "antd/lib/input";
import message from "antd/lib/message";
import Modal from "antd/lib/modal";
import Dropdown from "antd/lib/dropdown";
import Popconfirm from "antd/lib/popconfirm";
import Select from "antd/lib/select";
import Table from "antd/lib/table";
import Tag from "antd/lib/tag";
import { deleteUser, getAllUsers, updateUser } from "../api/auth";
import FooterBar from "../components/helpdesk/FooterBar";
import HeaderBar from "../components/helpdesk/HeaderBar";
import IconBadge from "../components/helpdesk/IconBadge";
import ManagementPageSkeleton from "../components/management/ManagementPageSkeleton";
import StatsCards from "../components/knowledge-base/StatsCards";
import "../styles/helpdesk.scss";

const roleOptions = [
  { label: "Admin", value: "ADMIN" },
  { label: "Editor", value: "EDITOR" },
  { label: "Author", value: "AUTHOR" },
];

const roleFilterOptions = [
  { value: "all", label: "All Roles" },
  ...roleOptions,
];

const statusFilterOptions = [
  { value: "all", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

function mapUserRow(user) {
  return {
    id: user?.id || user?._id || user?.email,
    name: user?.name || "-",
    email: user?.email || "-",
    role: String(user?.role || "AUTHOR").toUpperCase(),
    isActive: user?.isActive !== false,
  };
}

function AdminUsersPage() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actioningId, setActioningId] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllUsers();
      const mapped = (Array.isArray(response) ? response : []).map(mapUserRow);
      setRows(mapped);
    } catch {
      message.error("Unable to load users.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const visibleRows = useMemo(() => {
    return rows.filter((item) => {
      const searchOk =
        !searchTerm.trim() ||
        [item.name, item.email, item.role]
          .join(" ")
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase());
      const roleOk = roleFilter === "all" || item.role === roleFilter;
      const statusOk =
        statusFilter === "all" ||
        (statusFilter === "active" && item.isActive) ||
        (statusFilter === "inactive" && !item.isActive);
      return searchOk && roleOk && statusOk;
    });
  }, [roleFilter, rows, searchTerm, statusFilter]);

  const stats = useMemo(() => {
    const activeCount = visibleRows.filter((item) => item.isActive).length;
    const inactiveCount = visibleRows.filter((item) => !item.isActive).length;
    return [
      {
        id: "total",
        label: "Total Users",
        value: visibleRows.length,
        tone: "default",
      },
      { id: "active", label: "Active", value: activeCount, tone: "success" },
      {
        id: "inactive",
        label: "Inactive",
        value: inactiveCount,
        tone: "warning",
      },
    ];
  }, [visibleRows]);

  const handleDelete = async (user) => {
    if (!user?.id) return;
    setActioningId(user.id);
    try {
      await deleteUser(user.id);
      message.success("User deleted successfully.");
      await loadUsers();
    } catch (error) {
      const statusCode = error?.response?.status;
      const backendMessage = error?.response?.data?.message;

      if (statusCode === 409) {
        message.error(backendMessage || "Cannot delete user with linked content");
      } else {
        message.error(backendMessage || "Unable to delete user.");
      }
    } finally {
      setActioningId("");
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      name: user.name,
      role: user.role,
      isActive: user.isActive ? "active" : "inactive",
    });
  };

  const handleUpdate = async (values) => {
    if (!editingUser?.id) return;
    setActioningId(editingUser.id);
    try {
      await updateUser(editingUser.id, {
        name: values.name,
        role: values.role,
        isActive: values.isActive === "active",
      });
      message.success("User updated successfully.");
      setEditingUser(null);
      form.resetFields();
      await loadUsers();
    } catch {
      message.error("Unable to update user.");
    } finally {
      setActioningId("");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 220,
      ellipsis: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 260,
      ellipsis: true,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 120,
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 120,
      render: (value) =>
        value ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="orange">Inactive</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      render: (_, user) => (
        <div className="kb-table__actions">
          <Button size="small" onClick={() => openEdit(user)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete user"
            description="Are you sure?"
            onConfirm={() => handleDelete(user)}
            okText="Delete"
            okButtonProps={{ danger: true, loading: actioningId === user.id }}
          >
            <Button danger size="small" loading={actioningId === user.id}>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const getUserMenuItems = () => [
    { key: "edit", label: "Edit" },
    { key: "delete", label: "Delete", danger: true },
  ];

  const onUserMenuClick = (user, event) => {
    if (event.key === "edit") {
      openEdit(user);
      return;
    }

    Modal.confirm({
      title: "Delete user",
      content: "Are you sure?",
      okText: "Delete",
      okButtonProps: { danger: true, loading: actioningId === user.id },
      onOk: () => handleDelete(user),
    });
  };

  const showManagementSkeleton = loading && rows.length === 0;

  return (
    <div className="helpdesk-page">
      <HeaderBar />
      <main className="helpdesk-main">
        <div className="helpdesk-main__inner">
          <section className="kb-page" aria-labelledby="users-title">
            {showManagementSkeleton ? (
              <ManagementPageSkeleton statsCount={3} toolbarCount={3} rowCount={6} />
            ) : (
              <>
                <div className="kb-page__header">
                  <div>
                    <h1 id="users-title">User Management</h1>
                    <p>Manage user accounts and access control</p>
                  </div>
                  <Button
                    type="primary"
                    className="kb-page__action"
                    onClick={() => navigate("/admin/users/create")}
                  >
                    Create User
                  </Button>
                </div>

                <section className="kb-page__overview" aria-label="Users overview">
                  <div className="kb-page__overview-copy">
                    <h2>User Dashboard</h2>
                    <p>Review users, adjust roles, and maintain account status.</p>
                  </div>
                  <div className="kb-page__overview-meta">
                    <span>{visibleRows.length} user(s)</span>
                  </div>
                </section>

                <div className="kb-page__workspace">
                  <StatsCards stats={stats} />

                  <section className="kb-articles" aria-label="Users table">
                    <div className="kb-articles__header">
                      <h2>All Users</h2>
                      <p>{visibleRows.length} total</p>
                    </div>

                    <div className="kb-articles__toolbar kb-articles__toolbar--users">
                      <Input.Search
                        placeholder="Search users..."
                        enterButton={false}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                      />
                      <Select
                        value={roleFilter}
                        options={roleFilterOptions}
                        onChange={setRoleFilter}
                      />
                      <Select
                        value={statusFilter}
                        options={statusFilterOptions}
                        onChange={setStatusFilter}
                      />
                    </div>

                    {visibleRows.length || loading ? (
                      <>
                        <Table
                          className="kb-table"
                          rowKey={(record) => record.id}
                          columns={columns}
                          dataSource={visibleRows}
                          scroll={{ x: 980 }}
                          loading={loading}
                          pagination={{ pageSize: 8 }}
                        />
                        <div
                          className="kb-table-mobile"
                          aria-label="Users mobile list"
                        >
                          {visibleRows.map((user) => (
                            <article key={user.id} className="kb-mobile-card">
                              <div className="kb-mobile-card__head">
                                <h3 className="kb-mobile-card__title-text">
                                  {user.name}
                                </h3>
                                <div className="kb-mobile-card__head-right">
                                  {user.isActive ? (
                                    <Tag color="green">Active</Tag>
                                  ) : (
                                    <Tag color="orange">Inactive</Tag>
                                  )}
                                  <Dropdown
                                    menu={{
                                      items: getUserMenuItems(),
                                      onClick: (event) =>
                                        onUserMenuClick(user, event),
                                    }}
                                    trigger={["click"]}
                                    placement="bottomRight"
                                  >
                                    <button
                                      type="button"
                                      className="kb-mobile-card__menu-trigger"
                                      aria-label="User actions"
                                    >
                                      <IconBadge icon="more" />
                                    </button>
                                  </Dropdown>
                                </div>
                              </div>
                              <p className="kb-mobile-card__text">{user.email}</p>
                              <div className="kb-mobile-card__meta">
                                <span>{user.role}</span>
                              </div>
                            </article>
                          ))}
                        </div>
                      </>
                    ) : (
                      <Empty description="No users found" />
                    )}
                  </section>
                </div>
              </>
            )}
          </section>
        </div>
      </main>
      <FooterBar />

      <Modal
        title="Edit User"
        open={Boolean(editingUser)}
        onCancel={() => setEditingUser(null)}
        onOk={() => form.submit()}
        okButtonProps={{ loading: actioningId === editingUser?.id }}
        centered
        className="users-edit-modal"
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={handleUpdate}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please enter name." }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: "Please select role." }]}
          >
            <Select options={roleOptions} />
          </Form.Item>
          <Form.Item
            label="Status"
            name="isActive"
            rules={[{ required: true, message: "Please select status." }]}
          >
            <Select
              options={[
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default AdminUsersPage;
